import { useMemo } from 'react';
import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui/layout/Stack';
import { Box, Popover, useTheme } from '@/ui/primitives';
import { resolveChartPlotAreaBackgroundColor } from '@/ui/primitives/chartPlotAreaBackgroundColor';
import { withStatusFilterOptionIcons } from './statusFilterOptionIcons';
import { TagsFilterField } from './TagsFilterField';
import type { PopoverProps } from '@/ui/primitives';
import type { ReactElement } from 'react';

const popoverFontSize = 15;

type DropdownFilterOption = {
  value: string;
  label: string;
  count?: number;
};

type DropdownFiltersPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  selectedStatuses: string[];
  onStatusesChange: (next: string[]) => void;
  statusOptions: DropdownFilterOption[];
  statusLabel?: string;
  selectedEntities?: string[];
  onEntitiesChange?: (next: string[]) => void;
  entityOptions?: DropdownFilterOption[];
  entityLabel?: string;
  selectedItems?: string[];
  onItemsChange?: (next: string[]) => void;
  itemOptions?: DropdownFilterOption[];
  itemLabel?: string;
  selectedSuites?: string[];
  onSuitesChange?: (next: string[]) => void;
  suiteOptions?: DropdownFilterOption[];
  suiteLabel?: string;
  selectedTags: string[];
  onTagsChange: (next: string[]) => void;
  tagOptions: DropdownFilterOption[];
  selectedProjects?: string[];
  onProjectsChange?: (next: string[]) => void;
  projectOptions?: DropdownFilterOption[];
  projectLabel?: string;
  anchorOrigin?: PopoverProps['anchorOrigin'];
  transformOrigin?: PopoverProps['transformOrigin'];
  controlRadius?: string;
};

const isFilterPopperCloseEvent = (
  event: Parameters<NonNullable<PopoverProps['onClose']>>[0],
): boolean => {
  if (!('target' in event)) {
    return false;
  }
  const target = event.target;
  return (
    target instanceof Element &&
    target.closest(
      '[data-flowqa-filter-popper="true"], .MuiAutocomplete-popper, .MuiAutocomplete-paper, .MuiAutocomplete-listbox',
    ) !== null
  );
};

export const DropdownFiltersPopover = ({
  open,
  anchorEl,
  onClose,
  selectedStatuses,
  onStatusesChange,
  statusOptions,
  statusLabel = 'Latest Status',
  selectedEntities,
  onEntitiesChange,
  entityOptions,
  entityLabel = 'Item',
  selectedItems,
  onItemsChange,
  itemOptions,
  itemLabel = 'Item',
  selectedSuites,
  onSuitesChange,
  suiteOptions,
  suiteLabel = 'Suite',
  selectedTags,
  onTagsChange,
  tagOptions,
  selectedProjects,
  onProjectsChange,
  projectOptions,
  projectLabel = getFilterLabel('project'),
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
  controlRadius = r('md'),
}: DropdownFiltersPopoverProps): ReactElement => {
  const theme = useTheme();
  const toolbarFieldBackgroundColor = resolveChartPlotAreaBackgroundColor(theme);
  const hasSuiteFilter =
    selectedSuites !== undefined && onSuitesChange !== undefined && suiteOptions !== undefined;
  const hasEntityFilter =
    selectedEntities !== undefined && onEntitiesChange !== undefined && entityOptions !== undefined;
  const hasItemFilter =
    selectedItems !== undefined && onItemsChange !== undefined && itemOptions !== undefined;
  const hasProjectFilter =
    selectedProjects !== undefined &&
    onProjectsChange !== undefined &&
    projectOptions !== undefined;
  const showStatusFilter = statusOptions.length > 0;
  const statusOptionsWithIcons = useMemo(
    () => withStatusFilterOptionIcons(statusOptions),
    [statusOptions],
  );
  const handleClose: NonNullable<PopoverProps['onClose']> = (event) => {
    if (isFilterPopperCloseEvent(event)) {
      return;
    }
    onClose();
  };
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      sx={{
        '& .MuiPopover-paper': {
          borderRadius: `${controlRadius} !important`,
          overflow: 'visible !important',
        },
      }}
      paperSx={{
        backgroundColor: toolbarFieldBackgroundColor,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${controlRadius} !important`,
        overflow: 'visible !important',
        boxShadow: 'none',
        minWidth: 0,
        width: 'fit-content',
      }}
    >
      <Stack gap="none" sx={{ borderRadius: `${controlRadius} !important`, overflow: 'visible' }}>
        <Box
          sx={{
            pl: s('lg'),
            pr: s('lg'),
            py: s('lg'),
            width: 'fit-content',
            minWidth: 'fit-content',
          }}
        >
          <Stack gap="md" sx={{ justifyContent: 'flex-start', alignItems: 'flex-end' }}>
            {hasSuiteFilter ? (
              <TagsFilterField
                label={suiteLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${suiteLabel.toLowerCase()}`}
                value={selectedSuites}
                options={suiteOptions}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={180}
                inputBackgroundColor={toolbarFieldBackgroundColor}
                dropdownBackgroundColor={toolbarFieldBackgroundColor}
                inputLeftPadding={s('xs')}
                controlRadius={controlRadius}
                onChange={onSuitesChange}
              />
            ) : null}
            {hasEntityFilter ? (
              <TagsFilterField
                label={entityLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${entityLabel.toLowerCase()}`}
                value={selectedEntities}
                options={entityOptions}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={180}
                inputBackgroundColor={toolbarFieldBackgroundColor}
                dropdownBackgroundColor={toolbarFieldBackgroundColor}
                inputLeftPadding={s('xs')}
                controlRadius={controlRadius}
                onChange={onEntitiesChange}
              />
            ) : null}
            {hasItemFilter ? (
              <TagsFilterField
                label={itemLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${itemLabel.toLowerCase()}`}
                value={selectedItems}
                options={itemOptions}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={180}
                inputBackgroundColor={toolbarFieldBackgroundColor}
                dropdownBackgroundColor={toolbarFieldBackgroundColor}
                inputLeftPadding={s('xs')}
                controlRadius={controlRadius}
                onChange={onItemsChange}
              />
            ) : null}
            {showStatusFilter ? (
              <TagsFilterField
                label={statusLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${statusLabel.toLowerCase()}`}
                value={selectedStatuses}
                options={statusOptionsWithIcons}
                onChange={onStatusesChange}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={180}
                inputBackgroundColor={toolbarFieldBackgroundColor}
                dropdownBackgroundColor={toolbarFieldBackgroundColor}
                inputLeftPadding={s('xs')}
                controlRadius={controlRadius}
              />
            ) : null}
            <TagsFilterField
              label={getFilterLabel('tags')}
              fontSizePx={popoverFontSize}
              labelPlacement="inline"
              labelControlGap={s('md')}
              inputAriaLabel="Filter by tags"
              value={selectedTags}
              options={tagOptions}
              onChange={onTagsChange}
              emptyLabel={
                tagOptions.length > 0 ? getFilterEmptyLabel('all') : getFilterEmptyLabel('tags')
              }
              controlWidthPx={180}
              inputBackgroundColor={toolbarFieldBackgroundColor}
              dropdownBackgroundColor={toolbarFieldBackgroundColor}
              inputLeftPadding={s('xs')}
              controlRadius={controlRadius}
            />
            {hasProjectFilter ? (
              <TagsFilterField
                label={projectLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${projectLabel.toLowerCase()}`}
                value={selectedProjects}
                options={projectOptions}
                onChange={onProjectsChange}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={180}
                inputBackgroundColor={toolbarFieldBackgroundColor}
                dropdownBackgroundColor={toolbarFieldBackgroundColor}
                inputLeftPadding={s('xs')}
                controlRadius={controlRadius}
              />
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Popover>
  );
};

export type { DropdownFilterOption, DropdownFiltersPopoverProps };
